import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useNews } from '../context/NewsContext';
import {
    Box,
    Typography,
    Button,
    useTheme
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { dedupeNewsByEditorialContent } from '../utils/newsDedupe';

/**
 * NEWS GRID COMPONENT (THE RAW DATA FEED - GENESIS OPTION A)
 * Concept: Industrial Brutalism / Logistics Terminal.
 * No shadows. Hard lines. Exposed metadata.
 * Refactored Jan 2026: Reverted to React State for reliable, simple control.
 * Mod Jan 2026: Removed hover effects as requested (Static State).
 * Mod Jan 2026: Added "Micro-Interaction" (Subtle Zoom + Red Title) via CSS.
 */
import SectionWrapper from './SectionWrapper';

const isVideoSource = (value = '') => {
    const v = String(value || '').toLowerCase();
    return /\.(mp4|webm|ogg)(\?.*)?$/i.test(v) || v.includes('youtube') || v.includes('vimeo');
};

const resolveGalleryUrl = (entry) => {
    if (!entry) return '';
    if (typeof entry === 'string') return entry;
    return entry.original || entry.url || entry.src || '';
};

/**
 * NEWS GRID COMPONENT (THE RAW DATA FEED - GENESIS OPTION A)
 * Concept: Industrial Brutalism / Logistics Terminal.
 * No shadows. Hard lines. Exposed metadata.
 * Refactored Jan 2026: Reverted to React State for reliable, simple control.
 * Mod Jan 2026: Removed hover effects as requested (Static State).
 * Mod Jan 2026: Added "Micro-Interaction" (Subtle Zoom + Red Title) via CSS.
 */
const NewsGrid = () => {
    const { publishedNews } = useNews();
    const theme = useTheme();
    const uniquePublishedNews = dedupeNewsByEditorialContent(publishedNews);

    // BRUTALIST TOKENS
    const RAW_GREY = '#F4F4F4'; // Construction backing
    const PURE_BLACK = '#000000';
    const BRAND_RED = '#CC0000'; // The R5 Signal
    const BORDER = '1px solid #000000';

    const stripHtmlToText = (value) => {
        const raw = (value || '').toString();
        return raw
            .replace(/<[^>]*>/g, ' ') // naive HTML strip
            .replace(/\s+/g, ' ')
            .trim();
    };

    const stripToExcerpt = (value, maxLen = 140) => {
        const text = stripHtmlToText(value);
        if (!text) return '';
        if (text.length <= maxLen) return text;
        return `${text.slice(0, maxLen - 1)}…`;
    };

    return (
        <SectionWrapper
            id="news-grid"
            sx={{
                backgroundColor: RAW_GREY,
                color: PURE_BLACK,
                padding: { xs: 4, md: 6 },
                // Harmonized Spacing: Balanced Top/Bottom to prevent content hitting edges
                pt: { xs: 6, md: 8 }, // Reduced slightly to save vertical space
                // paddingBottom: 'calc(var(--footer-h, 0px) + 2rem)', // REMOVED
                borderBottom: BORDER,
                fontFamily: 'monospace',
                justifyContent: 'flex-start', // Allow natural flow
                overflow: 'hidden',          // Outer container safety
                '& .section-body': {         // FORCE HIDE SCROLLBAR (User Requirement)
                    overflowY: 'hidden !important'
                }
            }}
        >
            {/* BACKGROUND TECHNICAL GRID (Rhythm: Texture) */}
            <Box sx={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.04) 1px, transparent 1px)',
                backgroundSize: '100px 100px',
                pointerEvents: 'none',
                zIndex: 0
            }} />

            <Box sx={{ position: 'relative', zIndex: 1, height: { xs: 'auto', md: '100%' }, display: 'flex', flexDirection: 'column' }}> {/* WRAP CONTENT */}
                {/* STANDARD SECTION HEADER */}
                <Box sx={{
                    mb: 3, // Reduced from 6 to save space
                    borderBottom: BORDER,
                    pb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            // Portal section-title system: compact, crisp, non-shouty.
                            fontSize: 'clamp(1.05rem, 0.55vw + 0.75rem, 1.35rem)',
                            letterSpacing: '-0.02em',
                            fontWeight: 800,
                            textTransform: 'none',
                            lineHeight: 1.05,
                            color: theme.palette.text.primary,
                            display: 'flex',
                            // Cursor-like marker: align to text base (not vertical center).
                            alignItems: 'flex-end',
                            textRendering: 'geometricPrecision',
                            WebkitFontSmoothing: 'antialiased',
                        }}
                    >
                        Notícias
                        <Box component="span" sx={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: '2px',
                            bgcolor: BRAND_RED,
                            ml: 1.5,
                            flex: '0 0 auto',
                            // Nudge up slightly so it sits like a cursor on the baseline.
                            mb: '0.12em',
                            boxShadow: '0 0 18px rgba(204,0,0,0.22)',
                        }} />
                    </Typography>

                    <Button
                        component={RouterLink}
                        to="/news"
                        sx={{
                            color: PURE_BLACK,
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            '&:hover': {
                                bgcolor: 'transparent',
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        [ VER TODOS ]
                    </Button>
                </Box>


                {/* THE CARGO GRID (ASYMMETRIC) */}
	                <Box sx={{
	                    display: 'grid',
	                    gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, // 4 Columns for better asymmetry control
	                    // FIXED: Use 1fr to fill available space instead of fixed pixels
	                    gridTemplateRows: { xs: 'auto', md: '1fr' },
	                    gap: 0,
                    borderLeft: BORDER,
                    flex: 1,      // Grow to fill available space
	                    minHeight: 0, // Prevent overflow
	                    // height: '100%' // REMOVED: Caused overflow when combined with flex parent
	                }}>
	                    {uniquePublishedNews.slice(0, 4).map((item) => {
	                        // 4-card grid: all cards are equal-sized (no hero card).
	                        // Cards should always fill their window (no letterbox bars).
	                        const imgFit = 'cover';
	                        const imgPosition = 'center';
                            const titleText = (item.title || '').toString().trim();
                            const subtitleText = item.subtitle ? stripHtmlToText(item.subtitle) : '';
                            const primaryMediaUrl =
                                item.image ||
                                (Array.isArray(item.gallery) && item.gallery.length ? resolveGalleryUrl(item.gallery[0]) : '') ||
                                '/images/hero.png';
                            const isPrimaryVideo = isVideoSource(primaryMediaUrl);

                            // Responsive (viewport-based) typography, but consistent across cards.
                            // We keep harmony by clamping lines instead of scaling by text length.
                            const titleFontSize = 'clamp(1.02rem, 0.55vw + 0.78rem, 1.30rem)';
                            const subtitleFontSize = 'clamp(0.88rem, 0.26vw + 0.72rem, 1.00rem)';

	                        return (
	                            <Box
	                                key={item.id}
	                                component={RouterLink}
                                to={`/news/${item.id}`}
                                className="news-card-container"
	                                sx={{
	                                    gridColumn: { xs: 'span 1', md: 'span 1' },
	                                    // Row spanning removed - all fit in one unified row
	                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    textDecoration: 'none',
                                    backgroundColor: 'white',
                                    borderRight: BORDER,
                                    borderBottom: BORDER,
                                    // borderBottom: BORDER, // DUPLICATE REMOVED
                                    height: { xs: 'auto', md: '100%' },     // Fill the grid cell
                                    minHeight: 0,       // Allow shrinking if needed
                                    cursor: 'pointer',
                                    color: PURE_BLACK,
                                    transition: 'background-color 0.3s ease',

	                                    // HOVER STATE (CSS ONLY)
	                                    '&:hover': {
	                                        '& .news-media': {
	                                            transform: 'scale(1.08)',
	                                            filter: 'contrast(1.1)'
	                                        },
	                                        '& .news-title': {
	                                            color: 'inherit',
                                        },
                                        '& .arrow-icon': {
                                            transform: 'translateX(4px)',
                                            color: BRAND_RED,
                                            opacity: 1
                                        }
                                    }
                                }}
                            >
	                                {/* IMAGE (VISUAL ATTACHMENT) */}
	                                <Box sx={{
	                                    // Fixed image rail:
	                                    // Previously the image height could vary because the text block grows and
	                                    // the image flexes/shrinks to accommodate it. By fixing the image height,
	                                    // we keep a straight, harmonic row on Desktop while still cropping from
	                                    // the edges (object-fit: cover, center).
	                                    flex: '0 0 auto',
	                                    height: {
	                                        xs: 'clamp(180px, 38vw, 280px)',
	                                        // Desktop grid: consume the removed metadata space with more image area.
	                                        md: 'clamp(220px, 13vw, 280px)'
	                                    },
	                                    width: '100%',
	                                    overflow: 'hidden',
	                                    borderBottom: BORDER,
	                                    position: 'relative',
	                                    // With cover, this becomes an edge-to-edge photo surface.
	                                    backgroundColor: '#000000',
	                                    lineHeight: 0, // avoid inline image baseline gaps
	                                }}>
                                        {isPrimaryVideo ? (
	                                        <Box
                                                component="video"
	                                            src={primaryMediaUrl}
	                                            aria-label={titleText || 'Vídeo da notícia'}
	                                            className="news-media"
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                preload="metadata"
	                                            sx={{
	                                                position: 'absolute',
	                                                inset: 0,
	                                                width: '100%',
	                                                height: '100%',
	                                                objectFit: imgFit,
	                                                objectPosition: imgPosition,
	                                                filter: 'none',
	                                                display: 'block',
	                                                transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), filter 0.3s ease',
	                                                transform: 'scale(1.06)',
                                                    pointerEvents: 'none',
	                                            }}
	                                        />
                                        ) : (
	                                        <Box
	                                            component="img"
	                                            src={primaryMediaUrl}
	                                            alt="attachment"
	                                            className="news-media"
                                                onError={(e) => {
                                                    // Prevent infinite loop if fallback is also missing.
                                                    if (e.currentTarget && e.currentTarget.src && e.currentTarget.src.includes('/images/hero.png')) return;
                                                    e.currentTarget.src = '/images/hero.png';
                                                }}
	                                            sx={{
	                                                position: 'absolute',
	                                                inset: 0,
	                                                width: '100%',
	                                                height: '100%',
	                                                objectFit: imgFit,
	                                                objectPosition: imgPosition,
	                                                filter: 'none',
	                                                display: 'block',
	                                                transition: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), filter 0.3s ease',
	                                                // Slight overscale to mask rare "baked-in" letterboxing from uploads.
	                                                transform: 'scale(1.06)'
	                                            }}
	                                        />
                                        )}
	                                </Box>

	                                {/* 3. CONTENT PAYLOAD */}
	                                <Box sx={{
	                                    p: 2.5, // slightly more compact
	                                    flex: 1,
	                                    display: 'flex',
	                                    flexDirection: 'column',
	                                    justifyContent: 'space-between' // Distribute text/arrow
	                                }}>
	                                    <Typography
	                                        variant="h6"
	                                        className="news-title"
	                                        sx={{
	                                            // Responsive title: grows on large/high-res viewports, stays readable on small screens.
                                            fontSize: titleFontSize,
	                                            // Lighter, more natural weight (less "Word-bold").
	                                            fontWeight: 500,
	                                            lineHeight: 1.32,
	                                            mb: 1.5,
	                                            // Override theme h6 uppercase: keep original title casing (more editorial, less heavy).
	                                            textTransform: 'none',
	                                            letterSpacing: '-0.005em',
                                            color: 'inherit',
	                                            transition: 'color 0.3s ease',
	                                            // Avoid mid-word cuts (hyphenation) and keep a clean wrap.
	                                            whiteSpace: 'normal',
	                                            overflow: 'visible',
	                                            textOverflow: 'unset',
	                                            overflowWrap: 'break-word',
	                                            wordBreak: 'normal',
	                                            hyphens: 'none',
	                                        }}
	                                    >
	                                        {titleText}
	                                    </Typography>

                                        {/* Description: prefer subtitle (deck) and fall back to excerpt from body */}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: '"Inter", sans-serif',
                                                // Responsive body: prevents "empty block" on large screens while keeping density on small.
                                                fontSize: subtitleFontSize,
                                                lineHeight: 1.5,
                                                opacity: 0.78,
                                                mb: 2.25,
	                                                // Professional truncation: avoid “...” and avoid mid-word cuts.
	                                                // Keep a stable block height, but without mask gradients (which can look like white smudges).
	                                                overflow: 'hidden',
	                                                textOverflow: 'clip',
	                                                overflowWrap: 'break-word',
	                                                wordBreak: 'normal',
	                                                hyphens: 'none',
	                                                maxHeight: 'calc(1.5em * 6)',
                                                flex: 1,
                                            }}
                                        >
                                            {item.subtitle ? subtitleText : stripToExcerpt(item.content, 180)}
                                        </Typography>

	                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto' }}>
	                                        {/* Helper text for small cards */}
	                                        <Typography variant="caption" sx={{ fontFamily: 'monospace', opacity: 0.5 }}>
	                                            {new Date(item.publishedAt || item.updatedAt || Date.now()).toLocaleDateString('pt-BR')}
	                                        </Typography>
	                                        <ArrowForwardIcon
	                                            className="arrow-icon"
	                                            sx={{
	                                                fontSize: '1.2rem',
	                                                color: 'inherit',
	                                                opacity: 0.78,
	                                                transition: 'all 0.25s ease',
	                                                ml: 'auto'
	                                            }}
	                                        />
                                    </Box>
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            </Box>
        </SectionWrapper>
    );
};

export default NewsGrid;
